// src/services/api.ts
import { ACS_LIGHT_API_BASE_PATH } from '../config';
import { User } from '../types/user';
import { indexedDBStorage, CURRENT_DETAIL_CUSTOMER_ID_KEY } from './storage/indexedDB';
import { logApiError, setAuthDiag } from '../utils/errorLogger';
import { tokenManager, fetchWithTokenRefresh } from './token-manager';
import { isObjectData, setMasterCache, withMasterCache } from './storage/masterCache';

// getloginuserinfo が返す authoritylist の「実際の形」を、エラーlog同梱用スナップショットに反映する。
// 「再ログインしても権限バナーが出る」ユーザーで、サーバが空を返しているのか／
// 形が想定（文字列配列）と違うのか／取得自体が失敗しているのかを、後続の任意のエラーlogから読み取れるようにする。
function captureLoginUserInfoDiag(data: any): void {
  try {
    const list = data?.user?.authoritylist;
    setAuthDiag({
      userno: data?.user?.userno,
      getLoginUserInfoStatus: data?.statuscode,
      specialuser: data?.user?.specialuser,
      authoritylistType: Array.isArray(list) ? 'array' : typeof list,
      authoritylistLength: Array.isArray(list) ? list.length : null,
      // 先頭数件だけ生値を記録（要素が文字列かオブジェクトか等の形を確認する）
      authoritylistSample: Array.isArray(list) ? list.slice(0, 3) : list,
    });
  } catch (_) {
    // 補足の失敗は無視
  }
}

/**
 * 詳細表示する顧客IDを保存する
 * @param customerId 顧客ID
 */
export const saveCurrentDetailCustomerId = async (customerId: number): Promise<void> => {
  try {
    await indexedDBStorage.setItem(CURRENT_DETAIL_CUSTOMER_ID_KEY, customerId.toString());
  } catch (error) {
    console.error(`詳細表示顧客IDの保存エラー:`, error);
  }
};

/**
 * 詳細表示する顧客IDを取得する
 * @returns 顧客ID
 */
export const getCurrentDetailCustomerId = async (): Promise<number | null> => {
  try {
    const idStr = await indexedDBStorage.getItem(CURRENT_DETAIL_CUSTOMER_ID_KEY);
    if (!idStr) return null;
    
    const id = parseInt(idStr, 10);
    return isNaN(id) ? null : id;
  } catch (error) {
    console.error(`詳細表示顧客IDの取得エラー:`, error);
    return null;
  }
};

// セッション状態の追跡
let apiSessionInitialized = false;

// 認証関連のAPIリクエスト
export const authAPI = {
  /**
   * ログイン可能な企業一覧を取得
   * @param loginid ユーザー名
   * @param password パスワード
   * @returns 企業リスト
   */
  getCompanyList: async (loginid: string, password: string) => {
    try {

      // リクエストオプション
      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify({ loginid, password }),
      };

      // APIセッションの状態
      const sessionStatus = apiSessionInitialized ? '既存セッション使用' : '新規セッション作成';

      const response = await fetch(`${ACS_LIGHT_API_BASE_PATH}/api2/authentication/selectlogincompanylist`, requestOptions);
      
      // レスポンスヘッダーを確認
      const headersMap: Record<string, string> = {};
      response.headers.forEach((value, name) => {
        headersMap[name] = value;
      });

      const data = await response.json();
      
      // APIセッションを初期化済みとしてマーク
      if (!apiSessionInitialized) {
        apiSessionInitialized = true;
      }
      
      if (data.statuscode !== 200) {
        
        // セッション初期化されている場合は別のアプローチを試す
        if (apiSessionInitialized && data.message === 'ログイン可能な企業がありません') {
          // セッションをリセットして再試行する処理をここに追加できます
          // ここではAPIセッションがリセットされたと仮定
        }
        
        return { success: false, message: data.message || 'ログイン可能な企業一覧の取得に失敗しました' };
      }
      
      // 企業リストが空の場合も特別に処理
      if (data.statuscode === 200 && (!data.companylist || data.companylist.length === 0)) {
        return { success: false, message: 'ログイン可能な企業がありません', companyList: [] };
      }
      
      return { 
        success: true, 
        companyList: data.companylist || [] 
      };
    } catch (error) {
      logApiError('businessCardAPI.getCompanyList', error);
      return { success: false, message: 'サーバーとの通信に失敗しました' };
    }
  },

  /**
   * ログインユーザー情報を取得する
   * ログイン後のトークンで自身の情報のみ取得する
   * @param token 認証トークン
   * @returns ログインユーザー情報
   */
  // logOnError: 失敗時に logApiError を出すか。認証ブートストラップは起動窓で一時失敗
  //   しやすく、AuthProvider が数回リトライするため、最終試行以外は false を渡して
  //   同一失敗のログが多重化（3倍化）するのを防ぐ。
  getLoginUserInfo: async (token?: string, logOnError: boolean = true): Promise<{ success: boolean; userInfo?: User; message?: string }> => {
    try {
      let response;
      if (token) {
        // 認証トークンをヘッダーに設定
        response = await fetch(`${ACS_LIGHT_API_BASE_PATH}/api2/authentication/getloginuserinfo`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache, no-store',
            'Pragma': 'no-cache'
          }
        });
      } else {
        // 自動トークン管理を使用。これ自体が認証ブートストラップなので、
        // 認証ゲート（起動バースト抑止）は skipGate で bypass する（自己デッドロック防止）。
        response = await fetchWithTokenRefresh(`${ACS_LIGHT_API_BASE_PATH}/api2/authentication/getloginuserinfo`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store',
            'Pragma': 'no-cache'
          }
        }, false, { skipGate: true });
      }

      const data = await response.json();

      captureLoginUserInfoDiag(data);

      if (data.statuscode !== 200) {
        return { success: false, message: data.message || 'ユーザー情報の取得に失敗しました' };
      }

      return {
        success: true,
        userInfo: {
          id:                      data.user.userno,
          name:                    data.user.username,
          userno:                  data.user.userno,
          username:                data.user.username,
          deptno:                  data.user.deptno,
          deptname:                data.user.deptname,
          mailaddress:             data.user.mailaddress,
          image:                   data.user.image,
          defaultcustomerassistno: data.user.defaultcustomerassistno,
          defaultfollowassistno:   data.user.defaultfollowassistno,
          skipadditionalinfoflag:  data.user.skipadditionalinfoflag,
          systemdataflag:          data.user.systemdataflag,
          authoritylist:           data.user.authoritylist ?? [],
          specialuser:             data.user.specialuser ?? false
        }
      };
    } catch (error) {
      // リトライ中の中間試行は logOnError=false。最終試行/単発呼び出しのみ記録する。
      if (logOnError) {
        logApiError('authAPI.getLoginUserInfo', error, `${ACS_LIGHT_API_BASE_PATH}/api2/authentication/getloginuserinfo`, 'POST');
      }
      return { success: false, message: 'サーバーとの通信に失敗しました' };
    }
  },

  /**
   * ログイン処理
   * @param loginid ログインID
   * @param password パスワード
   * @param companyid 企業ID
   * @returns ログイン結果
   */
  login: async (loginid: string, password: string, companyid: string): Promise<{success: boolean, message?: string, user?: User}> => {
    try {

      // まず通常のログイン処理を行う
      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify({ loginid, password, companyid }),
      };

      const response = await fetch(`${ACS_LIGHT_API_BASE_PATH}/api2/authentication/auth`, requestOptions);

      // レスポンスヘッダーを確認
      const headersMap: Record<string, string> = {};
      response.headers.forEach((value, name) => {
        headersMap[name] = value;
      });
 
      const data = await response.json();
      //('ログインレスポンス:', data);
      
      if (data.statuscode !== 200) {
        return { success: false, message: data.message || 'ログインに失敗しました' };
      }
      
      // 成功時にはトークンとリフレッシュトークンを保存（タイムスタンプ付き）
      const token = data.token;
      await tokenManager.saveTokenWithTimestamp(token, data.refreshtoken);
      await indexedDBStorage.setItem('companyId', companyid);
      
      // APIセッション初期化済みフラグを設定
      apiSessionInitialized = true;
      
      // デフォルトのユーザー情報を構築
      let userInfo: User = {
        id: 1,
        username: loginid,
        name: loginid.split('@')[0], // 簡易的な表示名
        selectedCompany: {
          companyid: companyid,
          companyname: '選択された企業'
        }
      };

      try {
        // 取得したトークンを使って、ログインユーザの情報を取得しuserInfoにセット
        const loginUserResult = await authAPI.getLoginUserInfo(token);

        if (loginUserResult.success && loginUserResult.userInfo) {
          userInfo = {
            ...loginUserResult.userInfo,              // 全フィールドをそのまま展開
            selectedCompany: userInfo.selectedCompany // ログイン時にセットした企業情報だけ維持
          };
        }
      } catch (profileError) {
        // ユーザー情報取得失敗でもログイン自体は続行
        console.error('ログインユーザー情報の取得に失敗:', profileError);
      }
      
      // 最終的なユーザー情報をIndexedDBに保存
      await indexedDBStorage.setItem('user', JSON.stringify(userInfo));
      
      return { 
        success: true,
        user: userInfo
      };
    } catch (error) {
      logApiError('businessCardAPI.login', error, `${ACS_LIGHT_API_BASE_PATH}/api2/authentication/auth`, 'POST');
      return { success: false, message: 'サーバーとの通信に失敗しました' };
    }
  },

  /**
   * パスワード再設定通知メール送信
   * @param loginid 登録メールアドレス（ログインID）
   * @returns 処理結果
   */
  sendPasswordResetNotice: async (loginid: string): Promise<{ success: boolean; notFound?: boolean; message?: string }> => {
    const url = `${ACS_LIGHT_API_BASE_PATH}/api2/authentication/sendresetnotice`;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify({ loginid }),
      });

      const data = await response.json();

      if (data.statuscode === 200) {
        return { success: true };
      }

      // statuscode=400 はログインID未入力／ユーザー未存在のどちらか
      return {
        success: false,
        notFound: data.statuscode === 400,
        message: data.message || 'パスワード再設定メールの送信に失敗しました'
      };
    } catch (error) {
      logApiError('authAPI.sendPasswordResetNotice', error, url, 'POST');
      return { success: false, message: 'サーバーとの通信に失敗しました' };
    }
  },

  /**
   * トークンのリフレッシュ
   * @returns 処理結果
   */
  refreshToken: async () => {
    // リフレッシュ実装は token-manager.refreshTokenDirect に一本化する（single-flight）。
    // RTは単回使用（新トークン取得で旧RT破棄）のため、複数経路が各自 /refresh を叩くと
    // 旧RTを潰し合い、2本目以降が statuscode:400（破棄済RT）になる。restoreSession からの
    // 本呼び出しも getValidToken 経路と同じ single-flight を共有させ、二重リフレッシュを根絶する。
    try {
      const ok = await tokenManager.refreshTokenDirect();
      return ok
        ? { success: true }
        : { success: false, message: 'トークンの更新に失敗しました' };
    } catch (error) {
      logApiError('businessCardAPI.refreshToken', error, `${ACS_LIGHT_API_BASE_PATH}/api2/authentication/refresh`, 'POST');
      return { success: false, message: 'サーバーとの通信に失敗しました' };
    }
  },

  /**
   * ログアウト処理
   */
  logout: async () => {
    try {
      await indexedDBStorage.removeItem('token');
      await indexedDBStorage.removeItem('refreshToken');
      await tokenManager.clearTokenTimestamp();
      await indexedDBStorage.removeItem('user');
      await indexedDBStorage.removeItem('companyId');
      await indexedDBStorage.removeItem('masterContactUsers');
      // 旧キーの掃除（現在は書き込んでいない。既存端末に残った分を消すためで、数リリース後に削除可）
      await indexedDBStorage.removeItem('contactUsers');
      await indexedDBStorage.removeItem('retiredUsers');
      await indexedDBStorage.removeItem('webTokenRefreshedAt');
      await indexedDBStorage.removeItem('rank_colors');
      await indexedDBStorage.removeItem('masterRanks');
      await indexedDBStorage.removeItem('masterAssistTexts_customerRemarks');
      await indexedDBStorage.removeItem('masterAssistTexts_followContent');
      await indexedDBStorage.removeItem('masterAppoints');
      await indexedDBStorage.removeItem('masterPurposes');
      await indexedDBStorage.removeItem('masterOptionFields');
      await indexedDBStorage.removeItem('masterKeywords_01');
      await indexedDBStorage.removeItem('masterKeywords_02');

      // APIセッション状態もリセット
      apiSessionInitialized = false;
    } catch (error) {
      console.error('Logout error:', error);
      apiSessionInitialized = false;
    }
  }
};

// 名刺関連のAPIリクエスト
export const businessCardAPI = {
  /**
   * 郵便番号から住所を取得する(zipcloud)
   * @param postalCode 郵便番号
   * @returns 住所情報
   */
  getAddressByPostalCode: async (postalCode: string) => {
    try {
      // 外部サービスのZipAddress.netのAPIを使用
      const response = await fetch(
          `https://zipcloud.ibsnet.co.jp/api/search?zipcode=${postalCode}`
      );
      const data = await response.json();
      if (data.status === 200 && data.results) {
        const result = data.results[0];
        const address = `${result.address1}${result.address2}${result.address3}`;
        return { success: true, address };
      }

      return { success: false, message: '該当する住所が見つかりませんでした' };
    } catch (error) {
      logApiError('businessCardAPI.getAddressByPostalCode', error, `https://zipcloud.ibsnet.co.jp/api/search?zipcode=${postalCode}`, 'GET');
      return { success: false, message: 'サーバーとの通信に失敗しました' };
    }
  },

  /**
   * 顧客重複チェック
   * @param data 重複チェック対象データ
   * @returns 重複結果
   */
  checkDuplicate: async (data: { mailaddress?: string, customername?: string, tel?: string }) => {
    try {
      const response = await fetchWithTokenRefresh(`${ACS_LIGHT_API_BASE_PATH}/api2/customer/checkduplicate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      return await response.json();
    } catch (error) {
      logApiError('businessCardAPI.checkDuplicate', error, `${ACS_LIGHT_API_BASE_PATH}/api2/customer/checkduplicate`, 'POST');
      return { success: false, message: 'サーバーとの通信に失敗しました' };
    }
  },

  /**
   * 名刺データをアップロード・登録する
   * @param cardData 名刺データ
   * @returns 処理結果
   */
  uploadBusinessCard: async (cardData: any) => {
    try {
      const response = await fetchWithTokenRefresh(`${ACS_LIGHT_API_BASE_PATH}/api2/customer/uploadbusinesscard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(cardData)
      }, /* skipRetry: 二重登録防止のため自動再試行しない */ true);

      return await response.json();
    } catch (error) {
      logApiError('businessCardAPI.uploadBusinessCard', error, `${ACS_LIGHT_API_BASE_PATH}/api2/customer/uploadbusinesscard`, 'POST');
      return { success: false, message: 'サーバーとの通信に失敗しました' };
    }
  },

  /**
   * 名刺画像を複数枚アップロードする（multipart/form-data）
   * @param uploadFiles 加工後画像の File 配列
   * @param param アップロードパラメータ（担当者・備考など）
   * @returns 処理結果
   */
  uploadImage: async (uploadFiles: File[], param: any, opts?: { signal?: AbortSignal; timeoutMs?: number }) => {
    try {
      const formData = new FormData();
      uploadFiles.forEach((file) => formData.append('uploadFiles', file, file.name));
      // param は application/json の Blob として送る
      // （文字列のまま append すると octet-stream 扱いになりサーバ側で変換できないため）
      formData.append('param', new Blob([JSON.stringify(param)], { type: 'application/json' }));

      // Content-Type は未指定（ブラウザが multipart の boundary を自動付与）
      // 通信環境が悪い場合に固まらないよう 30 秒でタイムアウト。UI のキャンセルボタン
      // から渡された signal でも中断できる。
      const response = await fetchWithTokenRefresh(`${ACS_LIGHT_API_BASE_PATH}/api2/customer/uploadimage`, {
        method: 'POST',
        body: formData
      }, /* skipRetry: 二重登録防止のため自動再試行しない */ true,
        { timeoutMs: opts?.timeoutMs ?? 30000, signal: opts?.signal });

      return await response.json();
    } catch (error: any) {
      // タイムアウト or ユーザーによるキャンセル（中断）は通信失敗とは区別して返す
      if (error?.name === 'AbortError') {
        return { success: false, aborted: true, message: 'アップロードを中断しました' };
      }
      logApiError('businessCardAPI.uploadImage', error, `${ACS_LIGHT_API_BASE_PATH}/api2/customer/uploadimage`, 'POST');
      return { success: false, message: 'サーバーとの通信に失敗しました' };
    }
  },

  /**
   * 顧客データを登録/更新/削除する
   * @param data 顧客データ
   * @returns 処理結果
   */
  editCustomer: async (data: any) => {
    try {
      const response = await fetchWithTokenRefresh(`${ACS_LIGHT_API_BASE_PATH}/api2/customer/edit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }, /* skipRetry: 二重更新防止のため自動再試行しない */ true);

      const res = await response.json();

      return res;
    } catch (error) {
      logApiError('businessCardAPI.editCustomer', error, `${ACS_LIGHT_API_BASE_PATH}/api2/customer/edit`, 'POST');
      return { success: false, message: 'サーバーとの通信に失敗しました' };
    }
  },

  /**
   * 顧客検索を実行する
   * @param params 検索パラメータ
   * @returns 検索結果
   */
  searchCustomers: async (params: any) => {
    const url = `${ACS_LIGHT_API_BASE_PATH}/api2/customer/search`;
    // 素早い画面遷移で通信が中断（TypeError: Load failed 等）されると顧客データが
    // 取得できないため、ネットワーク中断時は自動リトライする（検索は冪等なので安全）。
    const maxAttempts = 3;
    let lastError: unknown;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const response = await fetchWithTokenRefresh(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(params)
        });

        const data = await response.json();

        return data;
      } catch (error) {
        lastError = error;
        logApiError('businessCardAPI.searchCustomers', error, url, 'POST');
        if (attempt < maxAttempts) {
          await new Promise((resolve) => setTimeout(resolve, 600));
          continue;
        }
      }
    }
    return {
      statuscode: 400,
      message: lastError instanceof Error ? lastError.message : 'サーバーとの通信に失敗しました',
      customerlistsize: 0,
      customerlist: []
    };
  },

  /**
   * 顧客詳細情報を取得する
   * @param customerNo 顧客番号
   * @param updateViewDate 閲覧日更新（true:更新, false:更新しない）
   * @returns 顧客詳細情報
   */
  getCustomerDetail: async (customerNo: number, updateViewDate: boolean = false) => {
    try {
      const response = await fetchWithTokenRefresh(`${ACS_LIGHT_API_BASE_PATH}/api2/customer/select`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customerno: customerNo,
          updateViewDate: updateViewDate
        })
      });

      const data = await response.json();

      return data;
    } catch (error) {
      logApiError('businessCardAPI.getCustomerDetail', error, `${ACS_LIGHT_API_BASE_PATH}/api2/customer/select`, 'POST');
      return { 
        statuscode: 400, 
        message: error instanceof Error ? error.message : 'サーバーとの通信に失敗しました'
      };
    }
  },

  /**
   * 区分値名称を取得する
   * @param classification 分類
   * @returns 区分値名称情報
   */
  getComSystemMapperValueList: async (classification: string) => {
    // 区分値（メンテナンスメッセージ・UseChatGPT 等）は変更頻度の低いマスタ系。
    // メニュー表示ごとに MaintenanceMessageBanner と refreshCapturedEmergencyMode から
    // 毎回ネットワーク往復していたため、区分ごとにキャッシュ（5分TTL）して往復を畳む。
    // 区分値によって内容が異なるので、キャッシュキーは classification 単位で分ける。
    const cacheKey = `masterComSystemMapper:${classification}`;
    const url = `${ACS_LIGHT_API_BASE_PATH}/api2/common/getcomsystemmappervaluelist`;

    // データ有りの正常応答（statuscode===200）だけをマスタ扱いでキャッシュし、5分以内の
    // 再表示では往復を省く。「対象データが存在しません」等の非200ボディはキャッシュしない
    // ＝メンテナンスメッセージ/UseChatGPT の設定変更が最大5分反映されない事故を防ぐ
    // （非200時に発火が残っても、認証ゲートで cold-start バーストは既に抑止済み）。
    // 通信/トークン失敗も throw してキャッシュを汚さない。
    const fetchFresh = async (): Promise<any> => {
      try {
        const response = await fetchWithTokenRefresh(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            classification: classification,
          })
        });

        const data = await response.json();
        if (data?.statuscode === 200) {
          setMasterCache(cacheKey, data).catch(() => {});
        }
        return data;
      } catch (error) {
        logApiError('businessCardAPI.getComSystemMapperValueList', error, url, 'POST');
        throw error;
      }
    };

    try {
      return await withMasterCache<any>(cacheKey, isObjectData, fetchFresh);
    } catch (error) {
      return {
        statuscode: 400,
        message: error instanceof Error ? error.message : 'サーバーとの通信に失敗しました'
      };
    }
  },

  /**
   * 仮登録顧客の一覧取得（仮登録の本登録）
   * @param params 検索／ページング／ソート条件
   * @returns 仮登録顧客一覧
   */
  searchTemporaryCustomers: async (params: {
    searchBatchcreateno?: number;
    searchBatchcreatetype?: number;
    searchBatchcreatestatus?: number;
    searchCreatefunction?: number;
    searchCompanyname?: string;
    searchCustomername?: string;
    isAllUser?: boolean;
    page: number;
    viewCount?: number;
    sortKey?: number;
    sortOrder?: boolean;
  }) => {
    try {
      const response = await fetchWithTokenRefresh(`${ACS_LIGHT_API_BASE_PATH}/api2/customercompare/repage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(params)
      });
      return await response.json();
    } catch (error) {
      logApiError('businessCardAPI.searchTemporaryCustomers', error, `${ACS_LIGHT_API_BASE_PATH}/api2/customercompare/repage`, 'POST');
      return {
        statuscode: 400,
        message: error instanceof Error ? error.message : 'サーバーとの通信に失敗しました'
      };
    }
  },

  /**
   * 仮登録顧客の件数取得（通知バッジ用途）
   * searchTemporaryCustomers と同じ絞り込み条件で件数のみ返す軽量API。
   * @param params 検索条件（searchCreatefunction / isAllUser など）
   * @returns 件数（customertemplistsize）
   */
  countTemporaryCustomers: async (params: {
    searchBatchcreateno?: number;
    searchBatchcreatetype?: number;
    searchBatchcreatestatus?: number;
    searchCreatefunction?: number;
    searchCompanyname?: string;
    searchCustomername?: string;
    isAllUser?: boolean;
  }) => {
    try {
      const response = await fetchWithTokenRefresh(`${ACS_LIGHT_API_BASE_PATH}/api2/customercompare/count`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(params)
      });
      return await response.json();
    } catch (error) {
      logApiError('businessCardAPI.countTemporaryCustomers', error, `${ACS_LIGHT_API_BASE_PATH}/api2/customercompare/count`, 'POST');
      return {
        statuscode: 400,
        message: error instanceof Error ? error.message : 'サーバーとの通信に失敗しました'
      };
    }
  },

  /**
   * 仮登録顧客の明細取得（customerdata と duplicatedata を返す）
   * @param customerNo 仮登録顧客No（repage の customerno）
   * @returns 明細
   */
  selectTemporaryCustomer: async (customerNo: number) => {
    try {
      const response = await fetchWithTokenRefresh(`${ACS_LIGHT_API_BASE_PATH}/api2/customercompare/select`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ searchCustomerno: customerNo })
      });
      return await response.json();
    } catch (error) {
      logApiError('businessCardAPI.selectTemporaryCustomer', error, `${ACS_LIGHT_API_BASE_PATH}/api2/customercompare/select`, 'POST');
      return {
        statuscode: 400,
        message: error instanceof Error ? error.message : 'サーバーとの通信に失敗しました'
      };
    }
  },

  /**
   * 仮登録顧客の削除（画面「仮登録の本登録」の削除に相当）
   * @param customerNo 仮登録顧客No（repage の customerno）
   * @returns 処理結果（customerdata / duplicatedata を含む）
   */
  deleteTemporaryCustomer: async (customerNo: number) => {
    try {
      const response = await fetchWithTokenRefresh(`${ACS_LIGHT_API_BASE_PATH}/api2/customercompare/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ searchCustomerno: customerNo })
      }, /* skipRetry: 二重削除防止のため自動再試行しない */ true);
      return await response.json();
    } catch (error) {
      logApiError('businessCardAPI.deleteTemporaryCustomer', error, `${ACS_LIGHT_API_BASE_PATH}/api2/customercompare/delete`, 'POST');
      return {
        statuscode: 400,
        message: error instanceof Error ? error.message : 'サーバーとの通信に失敗しました'
      };
    }
  },

  /**
   * 仮登録顧客の本登録（画面「仮登録の本登録」の登録に相当）
   * customerno=仮登録顧客No、dupeCustomerno=重複する既存本登録顧客No を渡す。
   * @param data 登録データ（顧客データ＋備考選択・未更新フラグ・検索/ページング条件）
   * @returns 処理結果（次の仮登録顧客 customerdata と重複 duplicatedata を含む）
   */
  registTemporaryCustomer: async (data: any) => {
    try {
      const response = await fetchWithTokenRefresh(`${ACS_LIGHT_API_BASE_PATH}/api2/customercompare/regist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }, /* skipRetry: 二重本登録防止のため自動再試行しない */ true);
      return await response.json();
    } catch (error) {
      logApiError('businessCardAPI.registTemporaryCustomer', error, `${ACS_LIGHT_API_BASE_PATH}/api2/customercompare/regist`, 'POST');
      return {
        statuscode: 400,
        message: error instanceof Error ? error.message : 'サーバーとの通信に失敗しました'
      };
    }
  }
};